import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, Lock, AlertCircle, Loader, Eye, EyeOff, Shield } from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { login } from "../../services/auth.service";
import { validateAllMessages, REGEX } from "../../utils/validation";
import { TOAST_MESSAGES, VALIDATION_MESSAGES } from "../../constants/messages";

const validationSchema = {
  email: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.email.required },
    { test: (v: string) => REGEX.email.test(v), message: VALIDATION_MESSAGES.email.invalid },
  ],
  password: [
    { test: (v: string) => v.trim().length > 0, message: VALIDATION_MESSAGES.password.required },
  ],
};

const Login = () => {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminLogin = location.pathname === "/admin/login";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({ email: [], password: [] });
  const [touched, setTouched] = useState<Record<string, boolean>>({ email: false, password: false });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const validateField = (field: string, value: string): string[] => {
    const rules = validationSchema[field as keyof typeof validationSchema];
    if (!rules) return [];
    const fieldErrors: string[] = [];
    for (const rule of rules) {
      if (!rule.test(value)) {
        fieldErrors.push(rule.message);
      }
    }
    return fieldErrors;
  };

  const handleChange = (field: string, value: string) => {
    if (field === "email") setEmail(value);
    else setPassword(value);

    setApiError("");

    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleBlur = (field: string) => {
    const value = field === "email" ? email : password;
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    const allErrors = validateAllMessages({ email, password }, validationSchema);
    setErrors(allErrors);
    setTouched({ email: true, password: true });

    const hasErrors = Object.values(allErrors).some((msgs) => msgs.length > 0);
    if (hasErrors) return;

    setIsLoading(true);
    try {
      const { hostname } = window.location;
      const subdomain =
        hostname.includes("diagnomiq.ai") && hostname.split(".")[0] !== "diagnomiq"
          ? hostname.split(".")[0]
          : undefined;

      const portal = isAdminLogin ? "admin" : "standard";
      const result = await login(email, password, subdomain, portal);

      if (result.token) {
        toast.success(TOAST_MESSAGES.success.login);
        loginUser(result.token, result.role, result.isSubscribed, result.user, result.tenant);
        return;
      } else {
        setApiError(TOAST_MESSAGES.error.loginInvalid);
        toast.error(TOAST_MESSAGES.error.loginInvalid);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || TOAST_MESSAGES.error.loginFailed;
      setApiError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {/* Left Banner Section */}
      <div className="hidden lg:flex w-1/2 relative bg-black">
        <img
          src="/logo/DiagnomIQ-loginpage-banner.jpg"
          alt="Login Banner"
          className="w-full h-full object-cover opacity-80"
        />
        {isAdminLogin && (
          <div className="absolute inset-0 bg-black/40 flex items-end p-10">
            <div>
              <h2 className="text-white text-2xl font-bold">DiagnomIQ Admin Portal</h2>
              <p className="text-gray-300 text-sm mt-1">
                Hospital administration & management console
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Right Login Form Section */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8 overflow-y-auto">
        <div className="max-w-md w-full space-y-8 py-12">
          <div className="text-center">
            <img
              src="/logo/DiagnomIQ.png"
              alt="AI Patient Care Platform"
              className="mx-auto h-24 w-auto object-contain"
            />
            {isAdminLogin ? (
              <>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  <Shield className="h-3.5 w-3.5" />
                  Admin Portal
                </div>
                <p className="mt-3 text-sm text-gray-600">
                  Sign in with your administrator credentials
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm text-gray-600">
                Sign in to access your personalized precision care treatment system
              </p>
            )}
          </div>

          <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-100">
            {/* API Error */}
            {apiError && (
              <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {apiError}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    className={`pl-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                      touched.email && errors.email?.length
                        ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    }`}
                    placeholder="Enter your email"
                    autoComplete="email"
                    aria-invalid={!!(touched.email && errors.email?.length)}
                    aria-describedby={errors.email?.length ? "email-error" : undefined}
                  />
                </div>
                {touched.email && errors.email?.length > 0 && (
                  <div id="email-error" className="mt-1 space-y-0.5">
                    {errors.email.map((msg, i) => (
                      <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        {msg}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    onBlur={() => handleBlur("password")}
                    className={`pl-10 py-3 block w-full rounded-md sm:text-sm transition-colors ${
                      touched.password && errors.password?.length
                        ? "border-red-300 text-red-900 placeholder-red-400 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    }`}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    aria-invalid={!!(touched.password && errors.password?.length)}
                    aria-describedby={errors.password?.length ? "password-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {touched.password && errors.password?.length > 0 && (
                  <div id="password-error" className="mt-1 space-y-0.5">
                    {errors.password.map((msg, i) => (
                      <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        {msg}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    isAdminLogin ? "Sign in as Admin" : "Sign in"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
